import {
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import dayjs from 'dayjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { SidebarHeaderComponent } from '../sidebar-header/sidebar-header.component';
import { FloatingInputComponent } from '../../inputs/floating-input/floating-input.component';
import { DropdownComponent } from '../../inputs/dropdown/dropdown.component';
import { InputCalendarComponent } from '../../inputs/input-calendar/input-calendar.component';
import { SimpleButtonComponent } from '../../buttons/simple-medium-button/simple-button.component';
import { InteractionBarStateService } from '../../../services/interaction-bar-state.service';
import { CREATE_RFI_PAGE, CreateUpdateRFIModel, RFI, RFI_STATUS } from '@app/store/rfis/rfi.types';
import { FloatingTextareaComponent } from '@app/framework/inputs/floating-textarea/floating-textarea.component';
import { TagDropdownComponent } from '@app/framework/inputs/tag-dropdown/tag-dropdown.component';
import { NotificationV2Service } from '@app/notification-v2.service';
import { RFIActions } from '@app/store/rfis/rfis.actions';
import { viewProjectSelectors } from '@app/store/view-project/view-project.selectors';
import { RFISelectors } from '@app/store/rfis/rfis.selectors';
import { DURATION_DAY_OPTIONS } from '@app/store/rfis/rfi.constants';
import { CurrentUserService } from '@app/services/current-user.service';
import { UserInvited } from '@app/store/invitations/invitations.types';
import { PresignedFileUploadService } from '@app/services/presigned-file-upload.service';
import {
  ManagedFile,
  UploadMetaData,
  isPersistedFile,
  isPendingFile,
} from '@app/framework/interfaces/file.type';
import { TagDropdownValue } from '@app/framework/interfaces/input.type';
import { InputUploadComponent } from '@app/framework/file-handling/input-upload/input-upload.component';

@Component({
  selector: 'app-create-rfi',
  imports: [
    SidebarHeaderComponent,
    FloatingInputComponent,
    DropdownComponent,
    InputCalendarComponent,
    SimpleButtonComponent,
    InputUploadComponent,
    NgClass,
    FormsModule,
    FloatingTextareaComponent,
    TagDropdownComponent,
  ],
  templateUrl: './create-rfi.component.html',
  styleUrl: './create-rfi.component.scss',
})
export class CreateRfiComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly notif = inject(NotificationV2Service);
  private readonly interactionBar = inject(InteractionBarStateService);
  private readonly userService = inject(CurrentUserService);
  private readonly actions$ = inject(Actions);
  private readonly presignedFileUploadService = inject(PresignedFileUploadService);

  readonly ngForm = viewChild<NgForm>('form');
  readonly rfi = input<RFI>();

  readonly currentPage = signal(1);
  readonly MAX_PAGES = signal(2).asReadonly();
  readonly CREATE_RFI_PAGE = CREATE_RFI_PAGE;
  readonly minStartDate = computed(() => {
    if (this.rfi()?.due_date) {
      return dayjs(this.rfi()?.due_date).format('YYYY-MM-DD');
    }
    return dayjs().format('YYYY-MM-DD');
  });

  readonly costImpactOptions = signal([
    { label: 'Yes', value: true },
    { label: 'No/TBD', value: false },
  ]).asReadonly();

  readonly scheduleImpactOptions = signal([
    { label: 'Yes', value: true },
    { label: 'No/TBD', value: false },
  ]).asReadonly();

  readonly projectId$ = this.store.select(viewProjectSelectors.selectProjectId);
  readonly projectId = toSignal(this.projectId$);
  readonly teamMembers = toSignal(this.store.select(viewProjectSelectors.selectTeamMembers));
  readonly invitedMembers = toSignal(this.store.select(viewProjectSelectors.selectInvitedMembers));
  readonly allUsers = toSignal(this.store.select(viewProjectSelectors.selectAllUsers));
  readonly nextRFINumber = toSignal(this.store.select(RFISelectors.selectNextRFINumber));
  readonly dayOptions = signal(DURATION_DAY_OPTIONS).asReadonly();
  protected RFINumberEditable = signal<boolean>(false);

  readonly initialModelValue: CreateUpdateRFIModel = {
    status: null,
    project_id: null,
    rfi_number: null,
    due_date: null,
    subject: null,
    rfi_manager_id: null,
    rfi_responsible_id: null,
    originator_user_id: null,
    originator_rfi_id: null,
    estimated_cost: null,
    estimated_schedule: null,
    question: null,
    drawing_id: null,
    section_reference: null,
    participant_ids: [],
  };

  model = linkedSignal<string, CreateUpdateRFIModel>({
    source: this.nextRFINumber,
    computation: (nextRFINumber, previous) => {
      const isEdit = !!this.rfi();
      const previousValue = !!previous?.value;
      if (isEdit && !previousValue) {
        return {
          ...this.initialModelValue,
          ...this.rfi(),
          participant_ids: this.rfi().participants?.map((p) => p.id) ?? [],
          rfi_manager_id: this.rfi().rfi_manager?.id,
          rfi_responsible_id: this.rfi().rfi_responsible?.id,
          originator_user_id: this.rfi().rfi_originator?.id,
        };
      }
      if (isEdit && previousValue) {
        return {
          ...previous?.value,
        };
      }

      return {
        ...(previous?.value ?? this.initialModelValue),
        rfi_manager_id: this.userService?.data?.id,
        rfi_number: nextRFINumber,
      };
    },
  });

  originalRFINumber = computed(() => {
    if (this.rfi()?.rfi_number) {
      return this.rfi().rfi_number;
    }
    return this.nextRFINumber();
  });

  costImpact = linkedSignal({
    source: this.rfi,
    computation: (rfi, previous) => {
      if (rfi?.id) {
        return !!rfi.estimated_cost;
      }
      return !!previous?.value;
    },
  });
  scheduleImpact = linkedSignal({
    source: this.rfi,
    computation: (rfi, previous) => {
      if (rfi?.id) {
        return !!rfi.estimated_schedule;
      }
      return !!previous?.value;
    },
  });
  wasEdited = signal(false);

  readonly participantOptionMembers = computed(() => {
    const invited = this.invitedMembers();
    return invited.filter((invitedMember) => {
      if (this.model().rfi_manager_id === invitedMember.id) {
        return false;
      }
      if (this.model().rfi_responsible_id === invitedMember.id) {
        return false;
      }
      return true;
    });
  });

  selectedParticipants: Signal<TagDropdownValue[]> = computed(() => {
    const participantIds = this.model().participant_ids;
    const invitedMembers = this.invitedMembers();
    return invitedMembers
      .filter((invitedMember) => participantIds.includes(invitedMember.id))
      .map((member) => ({
        name: `${member.name}, ${member.company_name}`,
        id: member.id,
      }));
  });

  availableParticipants = computed(() => {
    const members = this.participantOptionMembers();
    return members.map((member) => ({
      name: `${member.name}, ${member.company_name}`,
      id: member.id,
    }));
  });

  files = linkedSignal<RFI, ManagedFile[]>({
    source: this.rfi,
    computation: (rfi, previous) => {
      if (!previous?.value && rfi) {
        return rfi.question_files;
      }
      return [];
    },
  });
  newFiles = computed(() => {
    return this.files().filter(isPendingFile);
  });
  filesToDelete = new Set<number>();

  isDestroyed$ = new Subject<boolean>();
  ngOnInit() {
    this.projectId$.pipe(takeUntil(this.isDestroyed$)).subscribe((projectId) => {
      this.store.dispatch(RFIActions.onCreateRFISidebarOpened({ projectId }));
    });
  }
  ngOnDestroy() {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }

  RFINumberChanged(customId: string) {
    if (customId && typeof customId === 'string') {
      this.updateModel({ rfi_number: customId.toUpperCase() });
    }
  }

  onRFINumberIconClick() {
    // if (this.rfi()?.id) {
    //   // currently not allow editing RFI number if editing existing RFI
    //   return;
    // }

    if (this.RFINumberEditable()) {
      // if it was editable, reset to original
      this.updateModel({ rfi_number: this.originalRFINumber() });
    }
    this.RFINumberEditable.update((oldValue) => !oldValue);
  }

  goToPreviousPage() {
    if (this.currentPage() > CREATE_RFI_PAGE.FIRST_PAGE) {
      this.currentPage.update((current) => current - 1);
    }
  }
  goToNextPage() {
    this.ngForm()?.form?.get('due_date')?.markAsTouched();
    this.ngForm()?.form?.get('subject')?.markAsTouched();
    this.ngForm()?.form?.get('rfi_manager_id')?.markAsTouched();
    this.ngForm()?.form?.get('rfi_responsible_id')?.markAsTouched();
    if (this.ngForm()?.invalid) {
      this.notif.showError(
        'The form contains invalid fields. Please review and correct them before proceeding to the next step.',
      );
      return;
    }

    if (this.currentPage() < this.MAX_PAGES()) {
      this.currentPage.update((current) => current + 1);
    }
  }

  submitForm() {
    this.model.update((modelValue) => ({
      ...modelValue,
      status: RFI_STATUS.OPEN,
      project_id: this.projectId(),
    }));

    this.ngForm()?.form?.markAllAsTouched();

    if (this.ngForm().invalid) {
      this.notif.showError(
        'The form contains invalid fields. Please review and correct them before submitting.',
      );
      return;
    }

    this.saveAndUpload();
  }

  saveDraft() {
    this.model.update((modelValue) => ({
      ...modelValue,
      status: RFI_STATUS.DRAFT,
      project_id: this.projectId(),
    }));

    if (this.ngForm().invalid) {
      this.notif.showError(
        'The form contains invalid fields. Please review and correct them before submitting.',
      );
      return;
    }

    this.saveAndUpload();
  }

  private saveAndUpload() {
    if (!this.newFiles().length) {
      this.save(true);
      return;
    }

    this.save(false);

    // That's the kind of shit code I hate to read.
    // But it works, and I don't have time for a proper solution.
    this.actions$
      .pipe(
        takeUntil(this.isDestroyed$),
        ofType(RFIActions.createRFISuccess, RFIActions.updateRFISuccess),
        take(1),
      )
      .subscribe(({ rfi }) => {
        this.model.update((oldValue) => ({ ...oldValue, id: rfi?.id }));
        this.uploadFiles();
      });
  }

  private save(closeSidebar: boolean) {
    if (!this.rfi()) {
      this.store.dispatch(RFIActions.onCreateRFI({ data: this.model(), closeSidebar }));
    } else {
      this.store.dispatch(
        RFIActions.updateRFI({
          rfiId: this.rfi().id,
          rfi: this.model(),
          filesToDelete: [...this.filesToDelete.values()],
          closeSidebar,
        }),
      );
    }
  }

  private uploadFiles() {
    const metadata: Partial<UploadMetaData>[] = this.newFiles().map((_) => ({
      rfi: {
        id: this.model()?.id,
        belongs_to: 'question',
      },
    }));

    this.presignedFileUploadService
      .uploadMultipleFilesToS3(this.newFiles(), metadata)
      .subscribe(() => {
        this.interactionBar.close();
      });
  }

  updateModel(partialModel: Partial<CreateUpdateRFIModel>) {
    if (!this.wasEdited()) {
      this.interactionBar.setDisableClose(true);
      this.wasEdited.set(true);
    }

    this.model.update((currentValue) => ({
      ...currentValue,
      ...partialModel,
    }));
  }

  scheduleImpactChanged(enable: boolean) {
    this.scheduleImpact.set(enable);
    this.model.update((currentValue) => ({
      ...currentValue,
      estimated_schedule: enable ? 0 : null,
    }));
  }

  costImpactChanged(enable: boolean) {
    this.costImpact.set(enable);
    this.model.update((currentValue) => ({
      ...currentValue,
      estimated_cost: enable ? 0 : null,
    }));
  }

  onFilesChange(files: ManagedFile[]) {
    this.files.set(files as ManagedFile[]);
  }

  removeFile(event: { file: ManagedFile; index: number }) {
    this.files.update((files) => files.filter((_, i) => i !== event?.index));

    if (isPersistedFile(event.file)) {
      this.filesToDelete.add(event.file.id);
    }
  }

  removeParticipant(user: UserInvited) {
    this.model.update((currentValue) => ({
      ...currentValue,
      participant_ids: currentValue.participant_ids.filter((id) => id !== user.id),
    }));
  }

  onParticipantsChanged(selectedUsers: TagDropdownValue[]) {
    const selectedIds = selectedUsers.map((user) => user?.id).filter((userId) => !!userId);

    this.model.update((currentValue) => ({
      ...currentValue,
      participant_ids: selectedIds,
    }));
  }
}
